<template>
  <div class="approval-flow-config-management">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>审批流配置管理</span>
          <el-button type="primary" @click="handleCreate">新增配置</el-button>
        </div>
      </template>

      <!-- 搜索表单 -->
      <el-form :inline="true" :model="queryParams" class="search-form">
        <el-form-item label="企业" v-if="isSystemAdmin">
          <el-select
            v-model="queryParams.companyId"
            placeholder="请选择企业"
            clearable
            filterable
            style="width: 200px"
          >
            <el-option
              v-for="company in companyList"
              :key="company.id"
              :label="company.companyName"
              :value="company.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="流程类型">
          <el-select v-model="queryParams.processType" placeholder="全部" clearable style="width: 150px">
            <el-option label="合同审批" value="CONTRACT" />
            <el-option label="返货单审批" value="RETURN_ORDER" />
            <el-option label="报销申请审批" value="TRAVEL_EXPENSE" />
            <el-option label="出差申请审批" value="BUSINESS_TRIP" />
            <el-option label="成交台账审批" value="CUSTOMER_DEAL_RECORD" />
          </el-select>
        </el-form-item>
        <el-form-item label="流程名称">
          <el-input
            v-model="queryParams.processName"
            placeholder="请输入流程名称"
            clearable
            @keyup.enter="handleSearch"
            style="width: 200px"
          />
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="queryParams.status" placeholder="全部" clearable style="width: 120px">
            <el-option label="启用" :value="1" />
            <el-option label="禁用" :value="0" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">查询</el-button>
          <el-button @click="handleReset">重置</el-button>
        </el-form-item>
      </el-form>

      <!-- 配置列表 -->
      <el-table
        v-loading="loading"
        :data="configList"
        border
        style="width: 100%"
      >
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="companyName" label="企业名称" width="150" show-overflow-tooltip />
        <el-table-column prop="processType" label="流程类型" width="120">
          <template #default="{ row }">
            {{ getProcessTypeName(row.processType) }}
          </template>
        </el-table-column>
        <el-table-column prop="processName" label="流程名称" width="150" show-overflow-tooltip />
        <el-table-column label="审批节点" min-width="300">
          <template #default="{ row }">
            <div v-if="row.nodes && row.nodes.length > 0" class="node-list">
              <el-tag
                v-for="(node, index) in row.nodes"
                :key="node.id"
                :type="getNodeTagType(index)"
                style="margin-right: 5px; margin-bottom: 5px;"
              >
                {{ node.nodeOrder }}. {{ node.nodeName }} ({{ node.approverName }})
              </el-tag>
            </div>
            <span v-else style="color: #999;">暂无节点</span>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="80">
          <template #default="{ row }">
            <el-tag :type="row.status === 1 ? 'success' : 'danger'" size="small">
              {{ row.status === 1 ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="150" fixed="right">
          <template #default="{ row }">
            <el-button type="primary" size="small" @click="handleEdit(row)">编辑</el-button>
            <el-button type="danger" size="small" @click="handleDelete(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination">
        <el-pagination
          v-model:current-page="queryParams.page"
          v-model:page-size="queryParams.pageSize"
          :total="total"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />
      </div>
    </el-card>

    <!-- 创建/编辑配置对话框 -->
    <el-dialog
      v-model="dialogVisible"
      :title="dialogTitle"
      width="900px"
      @close="handleDialogClose"
    >
      <el-form
        ref="configFormRef"
        :model="configForm"
        :rules="configFormRules"
        label-width="120px"
      >
        <el-form-item label="企业" prop="companyId" v-if="isSystemAdmin">
          <el-select
            v-model="configForm.companyId"
            placeholder="请选择企业"
            filterable
            style="width: 100%"
            @change="handleCompanyChange"
          >
            <el-option
              v-for="company in companyList"
              :key="company.id"
              :label="company.companyName"
              :value="company.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="流程类型" prop="processType">
          <el-select 
            v-model="configForm.processType" 
            placeholder="请选择流程类型" 
            style="width: 100%"
            @change="handleProcessTypeChange"
          >
            <el-option label="合同审批" value="CONTRACT" />
            <el-option label="返货单审批" value="RETURN_ORDER" />
            <el-option label="报销申请审批" value="TRAVEL_EXPENSE" />
            <el-option label="出差申请审批" value="BUSINESS_TRIP" />
            <el-option label="成交台账审批" value="CUSTOMER_DEAL_RECORD" />
          </el-select>
        </el-form-item>
        <el-form-item label="流程名称" prop="processName">
          <el-input v-model="configForm.processName" />
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="configForm.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="描述">
          <el-input v-model="configForm.description" type="textarea" :rows="2" />
        </el-form-item>
        <el-form-item label="审批节点" prop="nodes" required>
          <div class="node-management">
            <el-alert
              v-if="isEdit"
              title="敏感操作提示"
              type="warning"
              :closable="false"
              class="sensitive-alert"
            >
              <template #default>
                <div>该操作为敏感操作，一天之内只允许修改5次！</div>
              </template>
            </el-alert>
            <div class="node-list-container">
              <div v-for="(node, index) in configForm.nodes" :key="index" class="node-item">
                <div class="node-item-header">
                  <div class="node-order-badge">
                    <span class="order-number">{{ node.nodeOrder }}</span>
                  </div>
                  <div class="node-controls">
                    <el-button
                      v-if="index > 0"
                      type="info"
                      size="small"
                      :icon="ArrowUp"
                      circle
                      @click="handleMoveNode(index, 'up')"
                      title="上移"
                    />
                    <el-button
                      v-if="index < configForm.nodes.length - 1"
                      type="info"
                      size="small"
                      :icon="ArrowDown"
                      circle
                      @click="handleMoveNode(index, 'down')"
                      title="下移"
                    />
                    <el-button
                      type="danger"
                      size="small"
                      :icon="Delete"
                      circle
                      @click="handleRemoveNode(index)"
                      title="删除节点"
                    />
                  </div>
                </div>
                <div class="node-item-body">
                  <el-row :gutter="15">
                    <el-col :span="12">
                      <div class="form-field-label">节点名称 <span class="optional-label">(选填)</span></div>
                      <el-input
                        v-model="node.nodeName"
                        placeholder="请输入节点名称（选填）"
                        clearable
                      />
                    </el-col>
                    <el-col :span="12">
                      <div class="form-field-label">审批人</div>
                      <el-select
                        v-model="node.approverId"
                        placeholder="请选择审批人"
                        filterable
                        clearable
                        @change="handleApproverChange(index)"
                      >
                        <el-option
                          v-for="user in filteredUserList"
                          :key="user.id"
                          :label="user.realName"
                          :value="user.id"
                        >
                          <span>{{ user.realName }}</span>
                          <span v-if="user.userType" class="user-type-tag">
                            {{ getUserTypeLabel(user.userType) }}
                          </span>
                        </el-option>
                      </el-select>
                    </el-col>
                  </el-row>
                </div>
              </div>
            </div>
            <el-button
              type="primary"
              size="default"
              :icon="Plus"
              @click="handleAddNode"
              class="add-node-btn"
            >
              添加节点
            </el-button>
            <div class="form-tip">
              <el-alert
                title="注意事项"
                type="info"
                :closable="false"
              >
                <template #default>
                  <ul class="tip-list">
                    <li>节点顺序从1开始，按顺序递增</li>
                    <li>不能连续出现2个同样的审批人</li>
                    <li>至少需要一个审批节点(如需审批)</li>
                    <li>业务员不参与审批流程</li>
                  </ul>
                </template>
              </el-alert>
            </div>
          </div>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="submitting">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus, Delete, ArrowUp, ArrowDown, User } from '@element-plus/icons-vue'
import {
  getApprovalFlowConfigList,
  createApprovalFlowConfig,
  updateApprovalFlowConfig,
  deleteApprovalFlowConfig,
  getApprovalFlowConfigById,
  getApprovalFlowConfigByCompanyAndProcess
} from '../api/approvalFlowConfig'
import { getAllCompanies } from '../api/company'
import { getUserList as getSysUserList } from '../api/sysUser'
import { getUserList as getManagerUserList, getCurrentCompany } from '../api/managerUser'

export default {
  name: 'ApprovalFlowConfigManagement',
  setup() {
    const loading = ref(false)
    const submitting = ref(false)
    const dialogVisible = ref(false)
    const isEdit = ref(false)
    const isSystemAdmin = ref(false)
    const currentUserCompanyId = ref(null)
    const configList = ref([])
    const companyList = ref([])
    const userList = ref([])
    const total = ref(0)

    const queryParams = reactive({
      companyId: null,
      processType: '',
      processName: '',
      status: null,
      page: 1,
      pageSize: 10
    })

    const configForm = reactive({
      id: null,
      companyId: null,
      processType: '',
      processName: '',
      status: 1,
      description: '',
      nodes: []
    })

    const configFormRef = ref(null)

    const configFormRules = computed(() => ({
      companyId: [{ required: isSystemAdmin.value, message: '请选择企业', trigger: 'change' }],
      processType: [{ required: true, message: '请选择流程类型', trigger: 'change' }],
      processName: [{ required: true, message: '请输入流程名称', trigger: 'blur' }],
      status: [{ required: true, message: '请选择状态', trigger: 'change' }],
      nodes: [
        {
          required: false,
          validator: (rule, value, callback) => {
            // 允许节点为空（代表无需审批）
            if (!value || value.length === 0) {
              callback()
              return
            }
            // 检查是否有连续相同的审批人
            for (let i = 0; i < value.length - 1; i++) {
              if (value[i].approverId && value[i + 1].approverId && 
                  value[i].approverId === value[i + 1].approverId) {
                callback(new Error(`节点 ${i + 1} 和节点 ${i + 2} 的审批人不能相同`))
                return
              }
            }
            callback()
          },
          trigger: 'change'
        }
      ]
    }))

    // 获取流程类型名称
    const getProcessTypeName = (type) => {
      const map = {
        'CONTRACT': '合同审批',
        'RETURN_ORDER': '返货单审批',
        'TRAVEL_EXPENSE': '报销申请审批',
        'BUSINESS_TRIP': '出差申请审批',
        'CUSTOMER_DEAL_RECORD': '成交台账审批'
      }
      return map[type] || type
    }

    // 获取节点标签类型
    const getNodeTagType = (index) => {
      const types = ['', 'success', 'warning', 'info', 'danger']
      const type = types[index % types.length]
      // 确保不返回 null 或 undefined，空字符串是有效的
      return type || ''
    }

    // 过滤后的用户列表（已经过滤掉业务员和系统管理员）
    const filteredUserList = computed(() => {
      return userList.value
    })

    // 获取用户类型标签
    const getUserTypeLabel = (userType) => {
      const map = {
        'MANAGER': '管理员',
        'ACCOUNTANT': '会计',
        'SYSTEM_ADMIN': '系统管理员',
        'ASSISTANT': '内勤',
        'SALESMAN': '业务员'
      }
      return map[userType] || userType
    }

    // 加载配置列表
    const loadConfigList = async () => {
      loading.value = true
      try {
        const res = await getApprovalFlowConfigList(queryParams)
        if (res?.code === '000000') {
          configList.value = res.data?.records || []
          total.value = res.data?.total || 0
        } else {
          ElMessage.error(res?.msg || res?.message || '获取配置列表失败')
        }
      } catch (error) {
        console.error('获取配置列表失败:', error)
        ElMessage.error('获取配置列表失败')
      } finally {
        loading.value = false
      }
    }

    // 加载企业列表
    const loadCompanyList = async () => {
      try {
        let companies = []
        
        // 根据用户角色使用不同的接口
        if (isSystemAdmin.value) {
          // 系统管理员：获取所有企业
          const res = await getAllCompanies()
          if (res?.code === '000000') {
            companies = res.data || []
          } else {
            ElMessage.error(res?.msg || res?.message || '获取企业列表失败')
            return
          }
        } else {
          // 管理员：只获取当前企业
          const res = await getCurrentCompany()
          if (res?.code === '000000') {
            const company = res.data
            if (company) {
              companies = [{
                id: company.id,
                companyName: company.companyName,
                companyShortName: company.companyShortName
              }]
            }
          } else {
            // 管理员获取企业失败时，不显示错误（可能是未关联企业），只记录日志
            console.warn('获取当前企业失败:', res?.msg || res?.message)
            companies = []
          }
        }
        
        companyList.value = companies
      } catch (error) {
        console.error('获取企业列表失败:', error)
        // 对于权限错误，不显示错误提示（避免弹窗过多）
        if (error.response?.status === 403) {
          console.warn('无权限获取企业列表，可能是权限限制')
        } else {
          ElMessage.error('获取企业列表失败')
        }
      }
    }

    // 加载用户列表
    const loadUserList = async (companyId = null) => {
      try {
        let res
        const params = { 
          page: 1, 
          pageSize: 1000
        }
        
        // 根据用户角色使用不同的接口
        if (isSystemAdmin.value) {
          // 系统管理员：获取所有用户
          if (companyId) {
            params.companyId = companyId
          }
          res = await getSysUserList(params)
        } else {
          // 管理员：只获取当前企业的用户
          res = await getManagerUserList(params)
        }
        
        if (res?.code === '000000') {
          // 过滤掉业务员和系统管理员
          userList.value = (res.data?.records || []).filter(user => 
            user.userType !== 'SALESMAN' && user.userType !== 'SYSTEM_ADMIN'
          )
        } else {
          // 对于权限错误，不显示错误提示（避免弹窗过多）
          if (res?.code === '000403') {
            console.warn('无权限获取用户列表，可能是权限限制')
            userList.value = []
          } else {
            ElMessage.error(res?.msg || res?.message || '获取用户列表失败')
          }
        }
      } catch (error) {
        console.error('获取用户列表失败:', error)
        // 对于权限错误，不显示错误提示（避免弹窗过多）
        if (error.response?.status === 403) {
          console.warn('无权限获取用户列表，可能是权限限制')
          userList.value = []
        } else {
          ElMessage.error('获取用户列表失败')
        }
      }
    }

    // 搜索
    const handleSearch = () => {
      queryParams.page = 1
      loadConfigList()
    }

    // 重置
    const handleReset = () => {
      queryParams.companyId = null
      queryParams.processType = ''
      queryParams.processName = ''
      queryParams.status = null
      queryParams.page = 1
      loadConfigList()
    }

    // 分页大小改变
    const handleSizeChange = () => {
      loadConfigList()
    }

    // 页码改变
    const handlePageChange = () => {
      loadConfigList()
    }

    // 创建
    const handleCreate = async () => {
      isEdit.value = false
      resetForm()
      // 非系统管理员时，默认设置当前用户的企业ID
      if (!isSystemAdmin.value && currentUserCompanyId.value) {
        configForm.companyId = currentUserCompanyId.value
      }
      dialogVisible.value = true
      // 打开对话框后，加载对应企业的用户列表
      if (configForm.companyId) {
        await loadUserList(configForm.companyId)
      } else if (!isSystemAdmin.value && currentUserCompanyId.value) {
        await loadUserList(currentUserCompanyId.value)
      }
    }

    // 编辑
    const handleEdit = async (row) => {
      isEdit.value = true
      try {
        const res = await getApprovalFlowConfigById(row.id)
        if (res?.code === '000000') {
          Object.assign(configForm, {
            id: res.data.id,
            companyId: res.data.companyId,
            processType: res.data.processType,
            processName: res.data.processName,
            status: res.data.status,
            description: res.data.description || '',
            nodes: (res.data.nodes || []).map(node => ({
              id: node.id,
              nodeOrder: node.nodeOrder,
              nodeName: node.nodeName,
              approverId: node.approverId,
              approverName: node.approverName
            }))
          })
          dialogVisible.value = true
          // 打开对话框后，加载对应企业的用户列表
          if (configForm.companyId) {
            await loadUserList(configForm.companyId)
          }
        } else {
          ElMessage.error(res?.msg || res?.message || '获取配置详情失败')
        }
      } catch (error) {
        console.error('获取配置详情失败:', error)
        ElMessage.error('获取配置详情失败')
      }
    }

    // 删除
    const handleDelete = async (row) => {
      try {
        await ElMessageBox.confirm(
          `确定要删除审批流配置"${row.processName}"吗？`,
          '删除确认',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )
        const res = await deleteApprovalFlowConfig(row.id)
        if (res?.code === '000000') {
          ElMessage.success('删除成功')
          loadConfigList()
        } else {
          ElMessage.error(res?.msg || res?.message || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除失败:', error)
          if (error.response?.data?.msg) {
            ElMessage.error(error.response.data.msg)
          } else {
            ElMessage.error('删除失败')
          }
        }
      }
    }

    // 检查是否已存在相同企业+流程类型的配置
    const checkDuplicateConfig = async () => {
      // 只在新增模式下检查，编辑模式不需要
      if (isEdit.value) {
        return false
      }
      
      // 如果企业ID或流程类型未选择，不检查
      if (!configForm.companyId || !configForm.processType) {
        return false
      }
      
      try {
        const res = await getApprovalFlowConfigByCompanyAndProcess(
          configForm.companyId,
          configForm.processType
        )
        
        // 如果返回了数据，说明已存在
        if (res?.code === '000000' && res.data) {
          const processTypeName = getProcessTypeName(configForm.processType)
          const companyName = companyList.value.find(c => c.id === configForm.companyId)?.companyName || '该企业'
          
          ElMessage.warning(`${companyName}的${processTypeName}已经配置过，无需重复添加`)
          
          // 清空流程类型选择
          configForm.processType = ''
          
          // 清除表单验证错误
          if (configFormRef.value) {
            configFormRef.value.clearValidate('processType')
          }
          
          return true
        }
        
        return false
      } catch (error) {
        console.error('检查配置是否存在失败:', error)
        // 检查失败不影响用户操作，不显示错误提示
        return false
      }
    }

    // 企业改变
    const handleCompanyChange = async () => {
      // 如果流程类型已选择，检查是否重复
      if (configForm.processType) {
        await checkDuplicateConfig()
      }
      // 企业改变时，重新加载该企业的用户列表
      if (configForm.companyId) {
        await loadUserList(configForm.companyId)
      }
    }
    
    // 流程类型改变
    const handleProcessTypeChange = async () => {
      // 如果企业已选择，检查是否重复
      if (configForm.companyId) {
        await checkDuplicateConfig()
      }
    }

    // 审批人改变
    const handleApproverChange = (index) => {
      const approverId = configForm.nodes[index].approverId
      const user = userList.value.find(u => u.id === approverId)
      if (user) {
        configForm.nodes[index].approverName = user.realName
      }
      // 触发表单验证
      if (configFormRef.value) {
        configFormRef.value.validateField('nodes')
      }
    }

    // 节点顺序改变
    const handleNodeOrderChange = () => {
      // 重新排序节点
      configForm.nodes.sort((a, b) => a.nodeOrder - b.nodeOrder)
      // 重新分配顺序
      configForm.nodes.forEach((node, index) => {
        node.nodeOrder = index + 1
      })
    }

    // 添加节点
    const handleAddNode = () => {
      const maxOrder = configForm.nodes.length > 0
        ? Math.max(...configForm.nodes.map(n => n.nodeOrder))
        : 0
      configForm.nodes.push({
        nodeOrder: maxOrder + 1,
        nodeName: '',
        approverId: null,
        approverName: ''
      })
    }

    // 删除节点
    const handleRemoveNode = async (index) => {
      // 如果是最后一个节点，需要确认
      if (configForm.nodes.length === 1) {
        try {
          await ElMessageBox.confirm(
            '删除最后一个审批节点后，该流程将无需审批，提交审批时将直接通过。是否确定删除所有审批节点？',
            '删除确认',
            {
              confirmButtonText: '确定删除',
              cancelButtonText: '取消',
              type: 'warning'
            }
          )
          // 用户确认删除
          configForm.nodes.splice(index, 1)
          // 触发表单验证
          if (configFormRef.value) {
            configFormRef.value.validateField('nodes')
          }
        } catch (error) {
          // 用户取消删除，不做任何操作
          if (error !== 'cancel') {
            console.error('删除节点失败:', error)
          }
        }
      } else {
        // 不是最后一个节点，直接删除
        configForm.nodes.splice(index, 1)
        // 重新分配顺序
        configForm.nodes.forEach((node, idx) => {
          node.nodeOrder = idx + 1
        })
        // 触发表单验证
        if (configFormRef.value) {
          configFormRef.value.validateField('nodes')
        }
      }
    }

    // 移动节点位置
    const handleMoveNode = (index, direction) => {
      if (direction === 'up' && index > 0) {
        const temp = configForm.nodes[index]
        configForm.nodes[index] = configForm.nodes[index - 1]
        configForm.nodes[index - 1] = temp
        // 更新顺序
        configForm.nodes.forEach((node, idx) => {
          node.nodeOrder = idx + 1
        })
      } else if (direction === 'down' && index < configForm.nodes.length - 1) {
        const temp = configForm.nodes[index]
        configForm.nodes[index] = configForm.nodes[index + 1]
        configForm.nodes[index + 1] = temp
        // 更新顺序
        configForm.nodes.forEach((node, idx) => {
          node.nodeOrder = idx + 1
        })
      }
      // 触发表单验证
      if (configFormRef.value) {
        configFormRef.value.validateField('nodes')
      }
    }

    // 提交
    const handleSubmit = async () => {
      if (!configFormRef.value) return
      
      await configFormRef.value.validate(async (valid) => {
        if (!valid) return

        // 如果有节点，验证节点数据完整性
        if (configForm.nodes.length > 0) {
          for (let i = 0; i < configForm.nodes.length; i++) {
            const node = configForm.nodes[i]
            // 节点名称改为非必填，不再验证
            if (!node.approverId) {
              ElMessage.warning(`请选择节点 ${i + 1} 的审批人`)
              return
            }
          }
        } else {
          // 如果没有节点，弹出确认对话框
          try {
            await ElMessageBox.confirm(
              '该流程无审批节点，代表无需审批，是否继续？',
              '确认提示',
              {
                confirmButtonText: '继续',
                cancelButtonText: '取消',
                type: 'warning'
              }
            )
          } catch (error) {
            // 用户取消，不继续提交
            if (error === 'cancel') {
              return
            }
            console.error('确认对话框错误:', error)
            return
          }
        }

        // 非系统管理员时，确保企业ID已设置
        if (!isSystemAdmin.value && !configForm.companyId && currentUserCompanyId.value) {
          configForm.companyId = currentUserCompanyId.value
        }

        submitting.value = true
        try {
          const data = {
            ...configForm,
            nodes: configForm.nodes && configForm.nodes.length > 0 
              ? configForm.nodes.map(node => ({
                  nodeOrder: node.nodeOrder,
                  nodeName: node.nodeName,
                  approverId: node.approverId,
                  approverName: node.approverName
                }))
              : [] // 确保节点数组不为 undefined
          }

          let res
          if (isEdit.value) {
            res = await updateApprovalFlowConfig(data)
          } else {
            res = await createApprovalFlowConfig(data)
          }

          if (res?.code === '000000') {
            ElMessage.success(isEdit.value ? '更新成功' : '创建成功')
            dialogVisible.value = false
            loadConfigList()
          } else {
            ElMessage.error(res?.msg || res?.message || (isEdit.value ? '更新失败' : '创建失败'))
          }
        } catch (error) {
          console.error('提交失败:', error)
          if (error.response?.data?.msg) {
            ElMessage.error(error.response.data.msg)
          } else {
            ElMessage.error(isEdit.value ? '更新失败' : '创建失败')
          }
        } finally {
          submitting.value = false
        }
      })
    }

    // 对话框关闭
    const handleDialogClose = () => {
      resetForm()
      if (configFormRef.value) {
        configFormRef.value.resetFields()
      }
    }

    // 重置表单
    const resetForm = () => {
      Object.assign(configForm, {
        id: null,
        companyId: null,
        processType: '',
        processName: '',
        status: 1,
        description: '',
        nodes: []
      })
    }

    // 初始化用户信息
    const initUserInfo = () => {
      const userInfoStr = localStorage.getItem('userInfo')
      if (userInfoStr) {
        const userInfo = JSON.parse(userInfoStr)
        isSystemAdmin.value = userInfo.userType === 'SYSTEM_ADMIN'
        currentUserCompanyId.value = userInfo.companyId ? Number(userInfo.companyId) : null
      }
    }

    onMounted(() => {
      initUserInfo()
      loadConfigList()
      // 根据用户角色决定是否加载企业列表和用户列表
      // 这些数据主要用于创建/编辑对话框，可以在打开对话框时再加载
      // 但为了更好的用户体验，在初始化时也加载（如果权限允许）
      loadCompanyList().catch(err => {
        // 静默处理权限错误，避免弹窗过多
        console.warn('初始化时加载企业列表失败:', err)
      })
      loadUserList().catch(err => {
        // 静默处理权限错误，避免弹窗过多
        console.warn('初始化时加载用户列表失败:', err)
      })
    })

    return {
      loading,
      submitting,
      dialogVisible,
      isEdit,
      isSystemAdmin,
      configList,
      companyList,
      userList,
      total,
      queryParams,
      configForm,
      configFormRef,
      configFormRules,
      dialogTitle: computed(() => isEdit.value ? '编辑审批流配置' : '新增审批流配置'),
      Plus,
      Delete,
      ArrowUp,
      ArrowDown,
      User,
      filteredUserList,
      getUserTypeLabel,
      getProcessTypeName,
      getNodeTagType,
      handleSearch,
      handleReset,
      handleSizeChange,
      handlePageChange,
      handleCreate,
      handleEdit,
      handleDelete,
      handleCompanyChange,
      handleProcessTypeChange,
      handleApproverChange,
      handleNodeOrderChange,
      handleAddNode,
      handleRemoveNode,
      handleMoveNode,
      handleSubmit,
      handleDialogClose
    }
  }
}
</script>

<style scoped>
.approval-flow-config-management {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.search-form {
  margin-bottom: 20px;
}

.node-list {
  display: flex;
  flex-wrap: wrap;
}

.node-management {
  width: 100%;
}

.sensitive-alert {
  margin-bottom: 20px;
  border-left: 4px solid #e6a23c;
}

.node-list-container {
  margin-bottom: 12px;
}

.node-item {
  margin-bottom: 12px;
  padding: 12px 15px;
  background: #ffffff;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.node-item:hover {
  border-color: #c0c4cc;
}

.node-item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.node-order-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  background: #409eff;
  border-radius: 50%;
}

.order-number {
  color: #ffffff;
  font-weight: bold;
  font-size: 12px;
}

.node-controls {
  display: flex;
  gap: 8px;
}

.node-controls .el-button {
  padding: 8px;
}

.node-item-body {
  padding-top: 0;
}

.form-field-label {
  font-size: 13px;
  color: #606266;
  margin-bottom: 6px;
  font-weight: 500;
}

.optional-label {
  color: #909399;
  font-weight: normal;
  font-size: 12px;
}

.add-node-btn {
  width: 100%;
  margin-top: 10px;
  height: 40px;
  font-size: 14px;
  border-radius: 4px;
}

.form-tip {
  margin-top: 20px;
}

.tip-list {
  margin: 0;
  padding-left: 20px;
  line-height: 1.8;
}

.tip-list li {
  margin-bottom: 4px;
  color: #606266;
}

.user-type-tag {
  float: right;
  font-size: 12px;
  color: #909399;
  margin-left: 10px;
}

.pagination {
  margin-top: 20px;
  text-align: right;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .node-item {
    padding: 15px;
  }
  
  .node-item-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  
  .node-controls {
    width: 100%;
    justify-content: flex-end;
  }
}
</style>

